class GoogleMapsLoader {
  constructor() {
    if (GoogleMapsLoader.instance) {
      return GoogleMapsLoader.instance;
    }

    this.apiKey = null;
    this.options = {};
    this.scriptLoaded = false;
    this.scriptLoading = false;
    this.loadingPromise = null;
    this.loadedLibraries = new Map();
    this.libraryLoadingPromises = new Map();
    this.callbackName = 'googleMapsApiReady';

    GoogleMapsLoader.instance = this;
  }

  /**
   * initialize the loader with your Google Maps API key
   * @param {string} apiKey - Google Maps API key
   * @param {Object} options - additional options (version, region, language)
   */
  init(apiKey, options = {}) {
    if (!apiKey) {
      throw new Error('Google Maps API key is required');
    }

    this.apiKey = apiKey;
    this.options = {
      version: options.version || 'weekly',
      region: options.region || '',
      language: options.language || '',
    };

    return this;
  }

  /**
   * load the base Google Maps script using the callback pattern for guaranteed readiness.
   * @returns {Promise<void>}
   */
  loadScript() {
    // if the script is already loaded or is loading, return the promise.
    if (this.scriptLoaded || this.scriptLoading) {
      return this.loadingPromise;
    }

    // check if script already exists and importLibrary is available
    if (window.google?.maps?.importLibrary) {
      this.scriptLoaded = true;
      return Promise.resolve();
    }

    if (!this.apiKey) {
      throw new Error(
        'Google Maps API key not initialized. Call GoogleMapsLoader.init(apiKey) first.',
      );
    }

    this.scriptLoading = true;
    this.loadingPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');

      // 1. define the global callback function
      window[this.callbackName] = () => {
        this.scriptLoaded = true;
        this.scriptLoading = false;
        delete window[this.callbackName]; // clean up the global function
        resolve();
      };

      const params = new URLSearchParams({
        callback: this.callbackName,
        key: this.apiKey,
        loading: 'async',
        v: this.options.version,
      });

      if (this.options.region) {
        params.append('region', this.options.region);
      }

      if (this.options.language) {
        params.append('language', this.options.language);
      }

      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.defer = true;

      // use onerror to catch network failures
      script.onerror = () => {
        this.scriptLoading = false;
        this.loadingPromise = null;
        delete window[this.callbackName];
        reject(new Error('Failed to load Google Maps script (network error).'));
      };

      document.head.appendChild(script);
    });

    return this.loadingPromise;
  }

  /**
   * load a single Google Maps library.
   * @param {string} libraryName - name of the library (e.g., 'maps', 'places', 'marker')
   * @returns {Promise<Object>} the loaded library object
   */
  async loadLibrary(libraryName) {
    // 1. return cached library if already loaded
    if (this.loadedLibraries.has(libraryName)) {
      return this.loadedLibraries.get(libraryName);
    }

    // 2. return existing loading promise if library is currently being loaded
    if (this.libraryLoadingPromises.has(libraryName)) {
      return this.libraryLoadingPromises.get(libraryName);
    }

    // 3. ensure base script is loaded first
    await this.loadScript();

    // 4. load the library using the modern importLibrary function
    const libraryPromise = (async () => {
      try {
        // check again to ensure the API is ready and exposed the function
        if (!window.google?.maps?.importLibrary) {
          throw new Error(
            'Google Maps API did not expose importLibrary function, possibly due to API key restrictions or delayed load.',
          );
        }

        const library = await window.google.maps.importLibrary(libraryName);

        // cache and clean up promise map
        this.loadedLibraries.set(libraryName, library);
        this.libraryLoadingPromises.delete(libraryName);
        return library;
      } catch (error) {
        this.libraryLoadingPromises.delete(libraryName);
        // throw the detailed error message, which was the final correction
        throw new Error(
          `Failed to load Google Maps library "${libraryName}": ${error.message || 'Unknown error during importLibrary call.'}`,
        );
      }
    })();

    // 5. store and return the promise
    this.libraryLoadingPromises.set(libraryName, libraryPromise);
    return libraryPromise;
  }

  /**
   * load multiple Google Maps libraries at once
   * @param {string[]} libraryNames - array of library names
   * @returns {Promise<Object[]>} array of loaded library objects in the same order
   */
  async loadLibraries(libraryNames) {
    if (!Array.isArray(libraryNames) || libraryNames.length === 0) {
      throw new Error('libraryNames must be a non-empty array');
    }

    // the map operation immediately starts the loading process for each library
    return Promise.all(libraryNames.map(name => this.loadLibrary(name)));
  }

  reset() {
    this.scriptLoaded = false;
    this.scriptLoading = false;
    this.loadingPromise = null;
    this.loadedLibraries.clear();
    this.libraryLoadingPromises.clear();
  }
}

const instance = new GoogleMapsLoader();
export default instance;
